import React, { useState } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { showToast } from '../components/Toast';
import '../css/event-modal.css';

function EventModal({ onClose, event = null, onSuccess }) {
  const [formData, setFormData] = useState({
    title: event?.title || '',
    date: event?.date || '',
    time: event?.time || '',
    location: event?.location || '',
    description: event?.description || '',
    longDescription: event?.longDescription || '',
    eventType: event?.eventType || 'concert',
    capacity: event?.capacity || '',
    image: event?.image || ''
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(event?.image || null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const eventTypes = {
    concert: '🎤 Concierto',
    festival: '🎵 Festival',
    workshop: '🎓 Taller',
    other: '📌 Otro'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('❌ Por favor selecciona una imagen válida', 'error', true);
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showToast('❌ La imagen debe ser menor a 5MB', 'error', true);
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const timestamp = Date.now();
      const imageName = `event_${timestamp}_${file.name}`;
      const imageRef = ref(storage, `events/${imageName}`);

      console.log('📤 Subiendo imagen a Firebase Storage...');
      const snapshot = await uploadBytes(imageRef, file);
      
      const downloadUrl = await getDownloadURL(snapshot.ref);
      console.log('✅ Imagen subida correctamente');
      
      return downloadUrl;
    } catch (error) {
      console.error('❌ Error al subir imagen:', error);
      
      if (error.code === 'storage/unauthorized') {
        showToast('❌ No tienes permiso para subir imágenes', 'error', true);
      } else if (error.code === 'storage/retry-limit-exceeded') {
        showToast('❌ Error de conexión. Intenta de nuevo', 'error', true);
      } else {
        showToast('❌ Error subiendo imagen: ' + error.message, 'error', true);
      }
      throw error;
    }
  };

  const deleteImageFromStorage = async (imageUrl) => {
    try {
      if (!imageUrl) return;
      
      const pathStart = imageUrl.indexOf('/o/') + 3;
      const pathEnd = imageUrl.indexOf('?alt=media');
      const imagePath = decodeURIComponent(imageUrl.substring(pathStart, pathEnd));
      
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      console.log('✅ Imagen eliminada de Storage');
    } catch (error) {
      console.error('⚠️ Error al eliminar imagen de Storage:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones
      if (!formData.title.trim()) {
        showToast('❌ El título es requerido', 'error', true);
        setLoading(false);
        return;
      }

      if (!formData.date) {
        showToast('❌ La fecha es requerida', 'error', true);
        setLoading(false);
        return;
      }

      if (!formData.time) {
        showToast('❌ La hora es requerida', 'error', true);
        setLoading(false);
        return;
      }

      if (!formData.location.trim()) {
        showToast('❌ La ubicación es requerida', 'error', true);
        setLoading(false);
        return;
      }

      if (!formData.description.trim()) {
        showToast('❌ La descripción es requerida', 'error', true);
        setLoading(false);
        return;
      }

      let imageUrl = formData.image;

      if (imageFile) {
        if (event?.image) {
          await deleteImageFromStorage(event.image);
        }

        imageUrl = await uploadImage(imageFile);
      }

      const eventData = {
        title: formData.title,
        date: formData.date,
        time: formData.time,
        location: formData.location,
        description: formData.description,
        longDescription: formData.longDescription,
        eventType: formData.eventType,
        capacity: formData.capacity,
        image: imageUrl,
        updatedAt: serverTimestamp()
      };

      if (event?.id) {
        const docRef = doc(db, 'events', event.id);
        await updateDoc(docRef, eventData);
        showToast('✅ Evento actualizado exitosamente', 'success', true);
      } else {
        await addDoc(collection(db, 'events'), {
          ...eventData,
          createdAt: serverTimestamp()
        });
        showToast('✅ Evento creado exitosamente', 'success', true);
      }

      setLoading(false);
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 500);
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);

      if (error.code === 'permission-denied') {
        showToast('❌ No tienes permiso para realizar esta acción', 'error', true);
      } else {
        showToast('❌ Error: ' + error.message, 'error', true);
      }
    }
  };

  const handleDelete = async () => {
    if (!event?.id) return;

    setLoading(true);
    try {
      if (event.image) {
        await deleteImageFromStorage(event.image);
      }

      await deleteDoc(doc(db, 'events', event.id));
      showToast('✅ Evento eliminado exitosamente', 'success', true);
      setLoading(false);
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 500);
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);

      if (error.code === 'permission-denied') {
        showToast('❌ No tienes permiso para eliminar este evento', 'error', true);
      } else {
        showToast('❌ Error al eliminar: ' + error.message, 'error', true);
      }
    }
  };

  return (
    <div className="event-modal-overlay" onClick={onClose}>
      <div className="event-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        {showDeleteConfirm ? (
          <div className="delete-confirm">
            <h3>⚠️ Confirmar eliminación</h3>
            <p>¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer.</p>
            <div className="delete-buttons">
              <button 
                className="btn-cancel" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                className="btn-delete" 
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="modal-header-compact">
              <h2>{event?.id ? '✏️ Editar Evento' : '➕ Crear Evento'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="event-form-compact">
              {/* Lado izquierdo - Preview e Imagen */}
              <div className="form-left">
                {imagePreview && (
                  <div className="image-preview-compact">
                    <img src={imagePreview} alt="Vista previa" />
                  </div>
                )}
                
                <div className="form-group-compact">
                  <label htmlFor="imageFile">🖼️ Imagen</label>
                  <div className="image-upload-wrapper-compact">
                    <input
                      type="file"
                      id="imageFile"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={loading}
                      className="image-input"
                    />
                    <label htmlFor="imageFile" className="image-upload-label-compact">
                      {imageFile ? '✓ ' + imageFile.name.substring(0, 15) : '📁 Seleccionar'}
                    </label>
                  </div>
                </div>

                {imagePreview && (
                  <button
                    type="button"
                    className="btn-remove-image-compact"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                      setFormData(prev => ({...prev, image: ''}));
                    }}
                    disabled={loading}
                  >
                    ✕ Remover
                  </button>
                )}

                <div className="form-group-compact">
                  <label htmlFor="eventType">🎭 Tipo de Evento</label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {Object.entries(eventTypes).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group-compact">
                  <label htmlFor="capacity">👥 Capacidad</label>
                  <input
                    type="number"
                    id="capacity"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    placeholder="Capacidad máxima"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Lado derecho - Campos de texto */}
              <div className="form-right">
                <div className="form-group-compact">
                  <label htmlFor="title">📝 Título</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Título del evento"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row-compact">
                  <div className="form-group-compact">
                    <label htmlFor="date">📅 Fecha</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group-compact">
                    <label htmlFor="time">🕐 Hora</label>
                    <input
                      type="time"
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group-compact">
                  <label htmlFor="location">📍 Ubicación</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Ubicación del evento"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group-compact">
                  <label htmlFor="description">📄 Descripción</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descripción breve"
                    rows="2"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group-compact">
                  <label htmlFor="longDescription">📖 Descripción Detallada</label>
                  <textarea
                    id="longDescription"
                    name="longDescription"
                    value={formData.longDescription}
                    onChange={handleChange}
                    placeholder="Descripción detallada (opcional)"
                    rows="2"
                    disabled={loading}
                  />
                </div>

                {/* Botones */}
                <div className="form-buttons-compact">
                  <button 
                    type="button" 
                    className="btn-cancel-compact" 
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  {event?.id && (
                    <button 
                      type="button" 
                      className="btn-delete-compact" 
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={loading}
                    >
                      🗑️
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="btn-submit-compact" 
                    disabled={loading}
                  >
                    {loading ? '...' : '✓'}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default EventModal;
